/**
 * Platform push notification API client (Web Push VAPID + FCM).
 * Aligns with pay platform /api/v1/push/* and /api/v1/fcm/* routes.
 */
import { resolveSharedAuthToken } from './crossDomainAuth';

const DEFAULT_API_BASE = '/api/v1';

export interface PushApiOptions {
  apiBase?: string;
  getAuthToken?: () => string | null;
}

export interface VapidSubscriptionPayload {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface VapidPushStatus {
  subscribed: boolean;
  subscriptionCount?: number;
}

export interface FcmDeviceInfo {
  userAgent?: string;
  platform?: string;
  language?: string;
}

export interface FcmStatus {
  subscribed: boolean;
  enabled?: boolean;
  hasSubscriptions?: boolean;
}

export type FcmSubscribeResult =
  | { ok: true }
  | { ok: false; reason: 'unauthenticated' | 'not_configured' | 'error'; message?: string };

function isJsonResponse(response: Response): boolean {
  const contentType = response.headers.get('content-type') || '';
  return contentType.includes('application/json');
}

function resolveOptions(options?: PushApiOptions) {
  return {
    apiBase: options?.apiBase ?? DEFAULT_API_BASE,
    getAuthToken: options?.getAuthToken ?? resolveSharedAuthToken,
  };
}

export function getPushAuthHeaders(options?: PushApiOptions): Record<string, string> {
  const { getAuthToken } = resolveOptions(options);
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function getJsonAuthHeaders(options?: PushApiOptions): Record<string, string> {
  return {
    ...getPushAuthHeaders(options),
    'Content-Type': 'application/json',
  };
}

export function buildFcmDeviceInfo(): FcmDeviceInfo {
  return {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    platform: 'web',
    language: typeof navigator !== 'undefined' ? navigator.language : undefined,
  };
}

/** GET /api/v1/push/vapid-key — public, no JWT required */
export async function fetchVapidPublicKey(
  options?: PushApiOptions
): Promise<string | null> {
  const { apiBase } = resolveOptions(options);

  try {
    const response = await fetch(`${apiBase}/push/vapid-key`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok || !isJsonResponse(response)) {
      return null;
    }

    const data = await response.json();
    return typeof data.publicKey === 'string' && data.publicKey.length > 0
      ? data.publicKey
      : null;
  } catch {
    return null;
  }
}

/** POST /api/v1/push/subscribe — JWT required */
export async function registerVapidSubscription(
  subscription: VapidSubscriptionPayload,
  options?: PushApiOptions
): Promise<boolean> {
  const { apiBase } = resolveOptions(options);

  try {
    const response = await fetch(`${apiBase}/push/subscribe`, {
      method: 'POST',
      headers: getJsonAuthHeaders(options),
      body: JSON.stringify(subscription),
    });

    return response.ok && isJsonResponse(response);
  } catch {
    return false;
  }
}

/** POST /api/v1/push/unsubscribe — JWT required; endpoint optional */
export async function unregisterVapidSubscription(
  endpoint?: string | null,
  options?: PushApiOptions
): Promise<boolean> {
  const { apiBase } = resolveOptions(options);

  try {
    const body = endpoint ? { endpoint } : {};
    const response = await fetch(`${apiBase}/push/unsubscribe`, {
      method: 'POST',
      headers: getJsonAuthHeaders(options),
      body: JSON.stringify(body),
    });

    return response.ok && isJsonResponse(response);
  } catch {
    return false;
  }
}

/** GET /api/v1/push/status — JWT required */
export async function fetchVapidPushStatus(
  options?: PushApiOptions
): Promise<VapidPushStatus> {
  const { apiBase } = resolveOptions(options);

  try {
    const response = await fetch(`${apiBase}/push/status`, {
      method: 'GET',
      headers: getPushAuthHeaders(options),
    });

    if (response.status === 401) {
      return { subscribed: false };
    }

    if (!response.ok || !isJsonResponse(response)) {
      return { subscribed: false };
    }

    const data = await response.json();
    return {
      subscribed: Boolean(data.subscribed),
      subscriptionCount:
        typeof data.subscriptionCount === 'number' ? data.subscriptionCount : undefined,
    };
  } catch {
    return { subscribed: false };
  }
}

/** POST /api/v1/push/test — JWT required */
export async function sendVapidTestNotification(options?: PushApiOptions): Promise<boolean> {
  const { apiBase } = resolveOptions(options);

  try {
    const response = await fetch(`${apiBase}/push/test`, {
      method: 'POST',
      headers: getJsonAuthHeaders(options),
    });

    if (!response.ok || !isJsonResponse(response)) {
      return false;
    }

    const data = await response.json();
    return data.success !== false;
  } catch {
    return false;
  }
}

/** POST /api/v1/fcm/subscribe — JWT required */
export async function registerFcmToken(
  token: string,
  deviceInfo?: FcmDeviceInfo,
  options?: PushApiOptions
): Promise<FcmSubscribeResult> {
  const { apiBase, getAuthToken } = resolveOptions(options);

  if (!getAuthToken()) {
    return { ok: false, reason: 'unauthenticated' };
  }

  try {
    const response = await fetch(`${apiBase}/fcm/subscribe`, {
      method: 'POST',
      headers: getJsonAuthHeaders(options),
      body: JSON.stringify({
        token,
        deviceInfo: deviceInfo ?? buildFcmDeviceInfo(),
      }),
    });

    if (response.status === 503 && isJsonResponse(response)) {
      const data = await response.json();
      if (data.error === 'FCM not configured' || data.success === false) {
        return { ok: false, reason: 'not_configured', message: data.error };
      }
    }

    if (!response.ok || !isJsonResponse(response)) {
      let message: string | undefined;
      if (isJsonResponse(response)) {
        const data = await response.json();
        message = data.message || data.error;
      }
      return { ok: false, reason: 'error', message };
    }

    return { ok: true };
  } catch (error) {
    return {
      ok: false,
      reason: 'error',
      message: error instanceof Error ? error.message : undefined,
    };
  }
}

/** POST /api/v1/fcm/unsubscribe — JWT required */
export async function unregisterFcmToken(options?: PushApiOptions): Promise<boolean> {
  const { apiBase } = resolveOptions(options);

  try {
    const response = await fetch(`${apiBase}/fcm/unsubscribe`, {
      method: 'POST',
      headers: getJsonAuthHeaders(options),
    });

    return response.ok && isJsonResponse(response);
  } catch {
    return false;
  }
}

/** GET /api/v1/fcm/status — JWT required */
export async function fetchFcmStatus(options?: PushApiOptions): Promise<FcmStatus> {
  const { apiBase } = resolveOptions(options);

  try {
    const response = await fetch(`${apiBase}/fcm/status`, {
      method: 'GET',
      headers: getPushAuthHeaders(options),
    });

    if (response.status === 401) {
      return { subscribed: false, enabled: false };
    }

    if (!response.ok || !isJsonResponse(response)) {
      return { subscribed: false };
    }

    const data = await response.json();
    return {
      subscribed: Boolean(data.subscribed),
      enabled: typeof data.enabled === 'boolean' ? data.enabled : undefined,
      hasSubscriptions:
        typeof data.hasSubscriptions === 'boolean'
          ? data.hasSubscriptions
          : Boolean(data.subscribed),
    };
  } catch {
    return { subscribed: false };
  }
}

/** POST /api/v1/fcm/test — JWT required */
export async function sendFcmTestNotification(options?: PushApiOptions): Promise<boolean> {
  const { apiBase } = resolveOptions(options);

  try {
    const response = await fetch(`${apiBase}/fcm/test`, {
      method: 'POST',
      headers: getJsonAuthHeaders(options),
    });

    if (!response.ok || !isJsonResponse(response)) {
      return false;
    }

    const data = await response.json();
    return data.success !== false;
  } catch {
    return false;
  }
}
